import { useMemo, useState } from 'react';
import { Box, Checkbox, Collapse, Typography } from '@mui/material';
import ListViewTableBodyRow from '../../../../Shared/Components/ListViewTable/ListViewTableBodyRow';
import ListViewTableBodyCell from '../../../../Shared/Components/ListViewTable/ListViewTableBodyCell';
import { TABLE_CELL_TYPOGRAPHY_STYLES } from '../styles';
import CellLink from '../../ProcessedTimesheets/Table/CellLink';
import { getSalesforceBookingLink, getSalesforceContactLink } from '../../../../utils/helpers';
import { COLORS } from '../../../../utils/Application_Constants';
import {
    SelectionStatus,
    useInvoicingSelectionContext,
} from '../../../Contexts/InvoicingSelectionContext';
import InvoicingItemCollapse from './InvoicingItemCollapse';
import AnimatedExpandIcon from '../../../../Shared/Components/AnimatedExpandIcon';
import useHierarchyConnectorVertical from '../hooks/useHierarchyConnectorVertical';

const getBookingType = (booking) => {
    const type = [];
    // if there's any normal PTS or expenses, add "General"
    if (booking.timesheets.length > 0 || booking.expenses.length > 0) {
        type.push('General');
    }
    // if any of the timesheets or expenses are marked as RT, add "RT",
    if (
        booking.timesheets.some((timesheet) => timesheet.rt) ||
        booking.expenses.some((expense) => expense?.items?.some((item) => item.rt))
    ) {
        type.push('RT');
    }
    return type;
};

const BookingContainerCollapse = ({ booking }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpand = () => {
        setIsExpanded((prevValue) => !prevValue);
    };
    const {
        lastChildRef,
        expandIconRef,
        hierarchyConnectorVerticalRef,
        mainContainerRef,
        renderConnector,
    } = useHierarchyConnectorVertical();

    const type = useMemo(() => getBookingType(booking), [booking]);
    const items = useMemo(
        () => [
            ...(booking.timesheets || []).map((timesheet) => ({
                type: 'timesheet',
                id: timesheet.id,
                data: timesheet,
            })),
            ...(booking.expenses || []).map((expense) => ({
                type: 'expense',
                id: expense.id,
                data: expense,
            })),
        ],
        [booking]
    );

    const { getBookingSelectionState, handleSelectBooking } = useInvoicingSelectionContext();
    const bookingSelectionStatus = getBookingSelectionState(booking.bookingId);

    return (
        <Box
            ref={mainContainerRef}
            sx={
                isExpanded
                    ? { backgroundColor: '#F5F6F7', pb: '10px', borderRadius: '5px' }
                    : undefined
            }
            position="relative"
        >
            <ListViewTableBodyRow
                sx={{ cursor: 'pointer', height: 80 }}
                onClick={toggleExpand}
                noBorder={isExpanded}
                aria-expanded={isExpanded}
                aria-controls={
                    isExpanded ? `booking-items-collapse-${booking.bookingId}` : undefined
                }
            >
                <ListViewTableBodyCell
                    sx={{
                        width: '90px',
                        pl: '16px !important',
                        pr: '2.5px !important',
                        flexGrow: '0 !important',
                        justifyContent: 'space-between',
                    }}
                >
                    <AnimatedExpandIcon ref={expandIconRef} isExpanded={isExpanded} />
                    <Checkbox
                        sx={{ p: 1 }}
                        checked={bookingSelectionStatus === SelectionStatus.SELECTED}
                        indeterminate={bookingSelectionStatus === SelectionStatus.INDETERMINATE}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSelectBooking(booking.bookingId);
                        }}
                    />
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={1}>
                    <Typography sx={{ ...TABLE_CELL_TYPOGRAPHY_STYLES, color: COLORS.LT_MIDNIGHT }}>
                        {booking.clientName}
                    </Typography>
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={1}>
                    <Typography sx={TABLE_CELL_TYPOGRAPHY_STYLES}>
                        {booking.clinicianSfId && booking.clinicianName ? (
                            <CellLink
                                href={getSalesforceContactLink(booking.clinicianSfId)}
                                label={booking.clinicianName}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            />
                        ) : (
                            booking.clinicianName
                        )}
                    </Typography>
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={1}>
                    <Typography
                        className="show-overflow"
                        sx={{ ...TABLE_CELL_TYPOGRAPHY_STYLES, display: 'flex' }}
                    >
                        {booking.bookingSfId && booking.bookingId ? (
                            <CellLink
                                href={getSalesforceBookingLink(booking.bookingSfId)}
                                label={`BKG-${booking.bookingId}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            />
                        ) : (
                            `BKG-${booking.bookingId || 'Unknown'}`
                        )}
                        &nbsp;({items.length})
                    </Typography>
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={1}>
                    <Typography sx={{ ...TABLE_CELL_TYPOGRAPHY_STYLES, color: COLORS.LT_MIDNIGHT }}>
                        {booking.numberOfLocations}
                    </Typography>
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={1}>
                    <Typography sx={{ ...TABLE_CELL_TYPOGRAPHY_STYLES, color: COLORS.LT_MIDNIGHT }}>
                        {booking.frequency || 'Weekly'}
                    </Typography>
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={1}>
                    <Typography sx={{ ...TABLE_CELL_TYPOGRAPHY_STYLES, color: COLORS.LT_MIDNIGHT }}>
                        {booking.collectorSfId && booking.collectorName && (
                            <CellLink
                                href={`https://example.com/${booking.collectorSfId}`}
                                label={booking.collectorName}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            />
                        )}
                    </Typography>
                </ListViewTableBodyCell>
                <ListViewTableBodyCell size={1}>
                    <Typography sx={{ ...TABLE_CELL_TYPOGRAPHY_STYLES, color: COLORS.LT_MIDNIGHT }}>
                        {type.join(', ')}
                    </Typography>
                </ListViewTableBodyCell>
            </ListViewTableBodyRow>

            <Collapse in={isExpanded} id={`booking-items-collapse-${booking.bookingId}`}>
                {renderConnector(isExpanded)}
                <Box sx={{ ml: '56px' }}>
                    {items.map((item, index) => (
                        <InvoicingItemCollapse
                            key={`bkg:${booking.bookingId}|${item.type}:${item.id}`}
                            bookingId={booking.bookingId}
                            type={item.type}
                            itemId={item.id}
                            data={item.data}
                            hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                            ref={index === items.length - 1 ? lastChildRef : null}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export default BookingContainerCollapse;
